import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, X, Wallet, Copy, CheckCircle2, AlertTriangle, History, ListOrdered } from "lucide-react";

/** Owner console → Affiliates tab. `oapi` is the owner-token axios instance. */
export default function AffiliatesTab({ oapi }) {
  const [view, setView] = useState("queue");
  const [data, setData] = useState({ rows: [], total_pending_gbp: 0, total_eligible_gbp: 0, min_payout_gbp: 10 });
  const [history, setHistory] = useState([]);
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const [q, h, e] = await Promise.all([
      oapi.get("/owner/affiliates/payouts"),
      oapi.get("/owner/affiliates/payouts/history"),
      oapi.get("/owner/affiliates/events", { params: { limit: 100 } }),
    ]);
    setData(q.data);
    setHistory(h.data);
    setEvents(e.data);
  };
  useEffect(() => { load().catch(() => toast.error("Failed to load affiliates")); }, []);

  const eligibleRows = data.rows.filter((r) => r.eligible);

  return (
    <div>
      <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
        <div>
          <span className="tape-label">AFFILIATES</span>
          <h2 className="font-display text-4xl mt-2">PAYOUT DESK</h2>
        </div>
        <div className="flex gap-2 items-center">
          {[["queue", "Queue", ListOrdered], ["history", "Payout history", History], ["ledger", "Ledger", Wallet]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setView(id)} data-testid={`affiliates-view-${id}`}
              className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 border flex items-center gap-1.5 ${view === id ? "border-[var(--primary)] text-[var(--primary)]" : "border-[var(--border-bright)] text-[var(--text-muted)] hover:text-white"}`}>
              <Icon className="w-3 h-3" /> {label}
            </button>
          ))}
          <button onClick={load} className="btn-ghost text-xs" data-testid="affiliates-refresh">
            <RefreshCw className="inline w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-px bg-[var(--border)] mb-6">
        <Tile label="Total pending" value={`£${data.total_pending_gbp.toFixed(2)}`} testid="affiliates-total-pending" />
        <Tile label={`Ready to pay (≥ £${data.min_payout_gbp})`} value={`£${data.total_eligible_gbp.toFixed(2)}`} accent testid="affiliates-total-eligible" />
        <Tile label="Referrers owed" value={eligibleRows.length} testid="affiliates-eligible-count" />
      </div>

      {view === "queue" && (
        <div className="panel">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-[var(--border)] text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
            <div className="col-span-3">Referrer</div>
            <div className="col-span-4">SOL wallet</div>
            <div className="col-span-1 text-right">Events</div>
            <div className="col-span-2 text-right">Pending £</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          <div className="divide-y divide-[var(--border)]">
            {data.rows.map((r) => (
              <button key={r.user_id} onClick={() => setSelected(r)} data-testid={`affiliate-row-${r.user_id}`}
                className="w-full text-left grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-[var(--surface)] transition-colors">
                <div className="col-span-3">
                  <div className="font-mono text-sm text-white">@{r.username}</div>
                  <div className="text-[10px] font-mono text-[var(--text-muted)]">{r.affiliate_code} · {r.email}</div>
                </div>
                <div className="col-span-4 font-mono text-[10px] text-[var(--text-muted)] truncate">
                  {r.payout_wallet || <span className="text-[var(--warning)] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> no wallet</span>}
                </div>
                <div className="col-span-1 text-right font-mono text-sm">{r.pending_count}</div>
                <div className="col-span-2 text-right font-display text-2xl text-[var(--primary)]">£{r.pending_gbp.toFixed(2)}</div>
                <div className="col-span-2 text-right">
                  <span className={`text-[9px] font-mono px-1.5 py-0.5 uppercase ${r.eligible ? (r.payout_wallet ? "bg-[var(--primary)] text-black" : "bg-[var(--warning)] text-black") : "border border-[var(--border-bright)] text-[var(--text-muted)]"}`}>
                    {r.eligible ? (r.payout_wallet ? "ready" : "needs wallet") : "below min"}
                  </span>
                </div>
              </button>
            ))}
            {data.rows.length === 0 && <div className="p-6 text-center text-xs font-mono text-[var(--text-muted)]" data-testid="affiliates-empty">No pending commissions</div>}
          </div>
        </div>
      )}

      {view === "history" && (
        <div className="panel divide-y divide-[var(--border)]">
          {history.map((p) => (
            <div key={p.id} className="px-4 py-3 grid grid-cols-12 gap-2 items-center text-xs font-mono" data-testid={`payout-${p.id}`}>
              <div className="col-span-3 text-white">@{p.username}</div>
              <div className="col-span-4 text-[10px] text-[var(--text-muted)] truncate">{p.tx_signature || p.wallet || "—"}</div>
              <div className="col-span-2 text-[var(--text-muted)]">{p.event_count} events</div>
              <div className="col-span-2 text-right text-[var(--primary)] font-display text-xl">£{p.amount_gbp.toFixed(2)}</div>
              <div className="col-span-1 text-right text-[10px] text-[var(--text-muted)]">{new Date(p.paid_at).toLocaleDateString()}</div>
            </div>
          ))}
          {history.length === 0 && <div className="p-6 text-center text-xs font-mono text-[var(--text-muted)]">No payouts yet</div>}
        </div>
      )}

      {view === "ledger" && (
        <div className="panel divide-y divide-[var(--border)] max-h-[70vh] overflow-y-auto">
          {events.map((e) => (
            <div key={e.id} className="px-4 py-2.5 grid grid-cols-12 gap-2 items-center text-xs font-mono" data-testid={`ledger-${e.id}`}>
              <div className="col-span-3 text-white">@{e.referrer_username} <span className="text-[var(--text-muted)]">← @{e.buyer_username}</span></div>
              <div className="col-span-3 text-[var(--text-muted)] uppercase">{e.source} · base £{e.base_amount_gbp.toFixed(2)}</div>
              <div className="col-span-2 text-[var(--primary)]">+£{e.commission_gbp.toFixed(2)}</div>
              <div className="col-span-2"><span className={`text-[9px] px-1.5 py-0.5 uppercase ${e.status === "paid" ? "bg-[var(--primary)] text-black" : "bg-[var(--warning)] text-black"}`}>{e.status}</span></div>
              <div className="col-span-2 text-right text-[10px] text-[var(--text-muted)]">{new Date(e.created_at).toLocaleString()}</div>
            </div>
          ))}
          {events.length === 0 && <div className="p-6 text-center text-xs font-mono text-[var(--text-muted)]">No commission events</div>}
        </div>
      )}

      {selected && <PayoutModal oapi={oapi} row={selected} onClose={() => setSelected(null)} onPaid={() => { setSelected(null); load(); }} />}
    </div>
  );
}

const Tile = ({ label, value, accent, testid }) => (
  <div className="bg-[var(--surface)] p-4" data-testid={testid}>
    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{label}</div>
    <div className={`font-display text-4xl mt-1 ${accent ? "text-[var(--primary)]" : "text-white"}`}>{value}</div>
  </div>
);

function PayoutModal({ oapi, row, onClose, onPaid }) {
  const [sig, setSig] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const copyWallet = async () => {
    try { await navigator.clipboard.writeText(row.payout_wallet); toast.success("Wallet copied."); }
    catch { toast.error("Copy failed"); }
  };

  const markPaid = async () => {
    if (!window.confirm(`Confirm you have sent £${row.pending_gbp.toFixed(2)} worth of SOL to @${row.username}?`)) return;
    setBusy(true);
    try {
      await oapi.post(`/owner/affiliates/${row.user_id}/mark-paid`, { tx_signature: sig || null, note: note || null });
      toast.success(`Marked £${row.pending_gbp.toFixed(2)} paid to @${row.username}.`);
      onPaid();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="panel-bright max-w-xl w-full" data-testid="payout-modal">
        <div className="px-6 py-3 border-b border-[var(--border-bright)] flex justify-between items-center">
          <div>
            <div className="font-display text-3xl">@{row.username}</div>
            <div className="text-[10px] font-mono text-[var(--text-muted)]">{row.email} · code {row.affiliate_code} · lifetime paid £{row.paid_gbp.toFixed(2)}</div>
          </div>
          <button onClick={onClose} className="text-[var(--text-muted)]" data-testid="payout-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-baseline justify-between">
            <span className="tape-label">OWED NOW</span>
            <span className="font-display text-5xl text-[var(--primary)]" data-testid="payout-amount">£{row.pending_gbp.toFixed(2)}</span>
          </div>
          <div>
            <span className="tape-label">SEND SOL TO</span>
            {row.payout_wallet ? (
              <div className="mt-2 flex gap-2">
                <code className="flex-1 text-[11px] font-mono text-white break-all bg-[var(--surface)] border border-[var(--border-bright)] p-3" data-testid="payout-wallet">{row.payout_wallet}</code>
                <button onClick={copyWallet} className="btn-ghost px-3" data-testid="payout-copy-wallet"><Copy className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="mt-2 text-xs font-mono text-[var(--warning)] flex items-center gap-2" data-testid="payout-no-wallet">
                <AlertTriangle className="w-4 h-4" /> User hasn't added a payout wallet yet. Roll this over to next month.
              </div>
            )}
          </div>
          {!row.eligible && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
              Below the £10 minimum — normally rolls over. You can still pay early.
            </div>
          )}
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">SOL tx signature (optional)</label>
            <input value={sig} onChange={(e) => setSig(e.target.value)} className="brutal" placeholder="Paste the transaction signature for your records" data-testid="payout-signature-input" />
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2">Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="brutal" placeholder="e.g. Feb payout run" data-testid="payout-note-input" />
          </div>
          <button onClick={markPaid} disabled={busy} className="btn-brutal w-full" data-testid="payout-mark-paid">
            <CheckCircle2 className="inline w-4 h-4 mr-2" /> {busy ? "Saving…" : `Mark £${row.pending_gbp.toFixed(2)} as Paid`}
          </button>
        </div>
      </div>
    </div>
  );
}
